import React from 'react';
import type {TableColumnsType} from 'antd';
import {Button, Table} from 'antd';
import {useQuery} from '@apollo/client';
import {OrderType} from '../../../../graphql/types/OrderType';
import {formatTimestampToDateTime} from '../../../../utils/dateTimeUtils';
import {t} from '../../../../i18n';
import {useStatusInternationalization} from '../../../delivery/parcels/hooks/useStatusInternationalization';
import {
    ExceptionDictionaryResponse,
    ExceptionDictionaryType
} from '../../../delivery/parcels/graphql/queries/getAllException';
import {GET_ALL_EXCEPTION_FOR_CS} from '../../graphql/queries/customerServiceSharedQueries';

interface CustomerServiceParcelColumnsProps {
    parcelData: OrderType[];
    showParcelDetail: (record: OrderType) => void;
    loading?: boolean;
}

export const CustomerServiceParcelColumns: React.FC<CustomerServiceParcelColumnsProps> = ({
    parcelData,
    showParcelDetail,
    loading,
}) => {
    const {data: exceptionData} = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION_FOR_CS);
    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters || [];
    const {
        getStatusText,
        getExceptionText,
        getExceptionStatusText,
        getExceptionReasonText,
        getNextActionTextFromBackend
    } = useStatusInternationalization();

    // 统一时间渲染：null/0/'0'/无效值 显示 '-'
    const renderTime = (value: any) => {
        const n = Number(value);
        if (!value || Number.isNaN(n) || n <= 0) return '-';
        return formatTimestampToDateTime(n);
    };

    const columns: TableColumnsType<OrderType> = [
        {
            title: t('customerservice.parcels.columns.trackingNumber'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            align: 'left',
            width: 150,
            render: (text, record) => (
                <Button type="link" onClick={() => showParcelDetail(record)} style={{ paddingLeft: 0 }}>
                    {text}
                </Button>
            ),
        },
        {
            title: t('customerservice.parcels.columns.parcelStatus'),
            dataIndex: 'overallStatus',
            key: 'overallStatus',
            align: 'left',
            width: 120,
            render: (status) => getStatusText(status),
        },
        {
            title: t('customerservice.parcels.columns.prePickupHub'),
            dataIndex: 'prePickupHubName',
            key: 'prePickupHubName',
            align: 'left',
            width: 140,
            render: (text) => text || '-',
        },
        {
            title: t('customerservice.parcels.columns.pickupHub'),
            dataIndex: 'pickupHubName',
            key: 'pickupHubName',
            align: 'left',
            width: 120,
            render: (text) => text || '-',
        },
        {
            title: t('customerservice.parcels.columns.firstSortCode'),
            key: 'sortingCodeSegment1',
            align: 'left',
            width: 120,
            render: (_, record) => {
                const parts = record.sortingCode?.split('-') || [];
                return parts[0] || '-';
            },
        },
        {
            title: t('customerservice.parcels.columns.secondSortCode'),
            key: 'sortingCodeSegment2',
            align: 'left',
            width: 120,
            render: (_, record) => {
                const parts = record.sortingCode?.split('-') || [];
                return parts[1] || '-';
            },
        },
        {
            title: t('customerservice.parcels.columns.thirdSortCode'),
            key: 'sortingCodeSegment3',
            align: 'left',
            width: 120,
            render: (_, record) => {
                const parts = record.sortingCode?.split('-') || [];
                return parts[2] || '-';
            },
        },
        {
            title: t('customerservice.parcels.columns.dsp'),
            dataIndex: ['dsp', 'name'],
            key: 'dspName',
            align: 'left',
            width: 100,
            render: (text) => text || '-',
        },
        {
            title: t('customerservice.parcels.columns.pickupArrivalDatetime'),
            dataIndex: 'receivedAtHub',
            key: 'receivedAtHub',
            align: 'left',
            width: 180,
            render: (text) => renderTime(text),
        },
        {
            title: t('customerservice.parcels.columns.destHubReceiveDatetime'),
            dataIndex: 'destHubArrivedAt',
            key: 'destHubArrivedAt',
            align: 'left',
            width: 180,
            render: (text) => renderTime(text),
        },
        {
            title: t('customerservice.parcels.columns.warehouseReceiveDatetime'),
            dataIndex: 'destWarehouseArrivedAt',
            key: 'destWarehouseArrivedAt',
            align: 'left',
            width: 180,
            render: (text) => renderTime(text),
        },
        {
            title: t('customerservice.parcels.columns.lastDeliveryTaskDate'),
            dataIndex: 'lastDeliveryScanTime',
            key: 'lastDeliveryTaskDate',
            align: 'left',
            width: 180,
            render: (text) => renderTime(text),
        },
        {
            title: t('customerservice.parcels.columns.lastDeliveryDatetime'),
            dataIndex: 'lastDeliveryScanTime',
            key: 'lastDeliveryScanTime',
            align: 'left',
            width: 180,
            render: (text) => renderTime(text),
        },
        {
            title: t('customerservice.parcels.columns.deliveredDatetime'),
            dataIndex: 'deliveredAt',
            key: 'deliveredAt',
            align: 'left',
            width: 180,
            render: (text) => renderTime(text),
        },
        {
            title: t('customerservice.parcels.columns.hasException'),
            dataIndex: 'isHasBeenSetException',
            key: 'isHasBeenSetException',
            align: 'left',
            width: 100,
            render: (value) => getExceptionText(value),
        },
        {
            title: t('customerservice.parcels.columns.exceptionStatus'),
            dataIndex: 'isAbnormal',
            key: 'isAbnormal',
            align: 'left',
            width: 120,
            render: (text, record) => {
                if (!record.isHasBeenSetException) {
                    return '-';
                }
                return getExceptionStatusText(text, record.exceptionStatus);
            },
        },
        {
            title: t('customerservice.parcels.columns.lastExceptionReason'),
            dataIndex: 'exceptionReasonCode',
            key: 'exceptionReasonCode',
            align: 'left',
            width: 150,
            render: (text, record) => {
                const exception = exceptionList?.find(
                    (exception: ExceptionDictionaryType) => String(exception.exceptionReasonCode) === String(record.exceptionReasonCode)
                );
                return getExceptionReasonText(exception, record.exceptionReasonCode);
            },
        },
        {
            title: t('customerservice.parcels.columns.lastExceptionDatetime'),
            dataIndex: 'markExceptionAt',
            key: 'markExceptionAt',
            align: 'left',
            width: 180,
            render: (text) => renderTime(text),
        },
        {
            title: t('customerservice.parcels.columns.nextAction'),
            dataIndex: 'nextAction',
            key: 'nextAction',
            align: 'left',
            width: 120,
            render: (text) => getNextActionTextFromBackend(text) || '-',
        },
        {
            title: t('customerservice.parcels.columns.orderTime'),
            dataIndex: 'orderTime',
            key: 'orderTime',
            align: 'left',
            width: 180,
            render: (text) => renderTime(text),
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={parcelData}
            rowKey="id"
            pagination={false}
            loading={loading}
            scroll={{ x: 'max-content' }}
         className="mb-4"/>
    );
};

